import React, { Suspense, useEffect, useRef } from "react";
import { ProtectedRoute } from "@/auth";
import { Switch, Route, Redirect, useLocation } from "react-router-dom";
import { page as analyticsPage, trackEvent } from "@/analytics";
import { NAVIGATION_EVENTS, SESSION_EVENTS } from "@/analytics/events";
import { useAuth0 } from "@auth0/auth0-react";

import {
  CoinbaseAuth,
  Login,
  Logout,
  Loading,

  // stripe callbacks
  StripeCancel,
  StripeSuccess,
  FeedbackScreen,
} from "@/components";
import { AttributionRedirect } from "@/components/AttributionRedirect";
import { AttributionTracker } from "@/utils/attribution";

import { useFeatureFlag, FEATURE_FLAG_KEYS, CURRENT_TAX_YEAR } from "@moonscape/shared";
import { PremiumTaxRoute } from "@/screens/TaxScreen/components/PremiumTaxRoute";
import { useOnboardingGate } from "@/contexts";

import { VerifyEmail } from "./components/VerifyEmail";

// -- Eager imports (critical path: dashboard, login, quick-start) --
import { DashboardScreen } from "@/screens/DashboardScreen/DashboardScreen";
import { QuickStartScreen } from "@/screens/QuickStartScreen";

// -- Lazy helper for named exports --
const lazyNamed = <T extends React.ComponentType<any>>(
  factory: () => Promise<{ [key: string]: T }>,
  name: string
) => React.lazy(() => factory().then((mod) => ({ default: (mod as any)[name] })));

// -- Lazy-loaded screens --
// Tax (heaviest: @react-pdf, @antv/g2plot)
const TaxScreen = lazyNamed(() => import("@/screens/TaxScreen/TaxScreen"), "TaxScreen");
const ProTaxScreen = lazyNamed(() => import("@/screens/TaxScreen/ProTaxScreen"), "ProTaxScreen");
const TaxSafeHarborScreen = lazyNamed(() => import("@/screens/TaxScreen/TaxSafeHarborScreen"), "TaxSafeHarborScreen");
const TaxLossHarvesting = lazyNamed(() => import("@/screens/TaxScreen/TaxLossHarvesting"), "TaxLossHarvesting");
const TaxEventsPage = lazyNamed(() => import("@/screens/TaxScreen/TaxEventsPage"), "TaxEventsPage");
const IncomeEventsPage = lazyNamed(() => import("@/screens/TaxScreen/IncomeEventsPage"), "IncomeEventsPage");
const ExcludedTransactionsView = lazyNamed(() => import("@/screens/TaxScreen/ExcludedTransactionsView"), "ExcludedTransactionsView");
const TaxAuditScreen = lazyNamed(() => import("@/screens/TaxScreen/TaxAuditScreen"), "TaxAuditScreen");
const TaxAssetBreakdownScreen = lazyNamed(() => import("@/screens/TaxScreen/TaxAssetBreakdownScreen"), "TaxAssetBreakdownScreen");
const TaxReportsOverviewScreen = lazyNamed(() => import("@/screens/TaxScreen/TaxReportsOverviewScreen"), "TaxReportsOverviewScreen");
const TaxReportHtmlScreen = lazyNamed(() => import("@/screens/TaxScreen/TaxReportHtmlScreen"), "TaxReportHtmlScreen");
const TaxReportHtmlRedirect = lazyNamed(() => import("@/screens/TaxScreen/TaxReportHtmlScreen"), "TaxReportHtmlRedirect");
const TaxComputationReportScreen = lazyNamed(() => import("@/screens/TaxScreen/TaxComputationReportScreen"), "TaxComputationReportScreen");

// Tax form PDF screens (pull in @react-pdf/renderer)
const TaxForm8949Screen = lazyNamed(() => import("@/screens/TaxScreen/TaxForm8949Screen"), "TaxForm8949Screen");
const TaxForm8949ReplicaScreen = lazyNamed(() => import("@/screens/TaxScreen/TaxForm8949ReplicaScreen"), "TaxForm8949ReplicaScreen");
const TaxFormScheduleDScreen = lazyNamed(() => import("@/screens/TaxScreen/TaxFormScheduleDScreen"), "TaxFormScheduleDScreen");
const TaxForm1040Screen = lazyNamed(() => import("@/screens/TaxScreen/TaxForm1040Screen"), "TaxForm1040Screen");
const TaxFormUsIncomeStatementScreen = lazyNamed(() => import("@/screens/TaxScreen/TaxFormUsIncomeStatementScreen"), "TaxFormUsIncomeStatementScreen");
const TaxFormUKScreen = lazyNamed(() => import("@/screens/TaxScreen/TaxFormUKScreen"), "TaxFormUKScreen");
const TaxFormSA100Screen = lazyNamed(() => import("@/screens/TaxScreen/TaxFormSA100Screen"), "TaxFormSA100Screen");
const TaxFormSchedule3Screen = lazyNamed(() => import("@/screens/TaxScreen/TaxFormSchedule3Screen"), "TaxFormSchedule3Screen");
const TaxFormSA108Screen = lazyNamed(() => import("@/screens/TaxScreen/TaxFormSA108Screen"), "TaxFormSA108Screen");
const TaxFormIncomeStatementScreen = lazyNamed(() => import("@/screens/TaxScreen/TaxFormIncomeStatementScreen"), "TaxFormIncomeStatementScreen");
const TaxFormLossWriteoffsScreen = lazyNamed(() => import("@/screens/TaxScreen/TaxFormLossWriteoffsScreen"), "TaxFormLossWriteoffsScreen");
const TaxFormAuditTrailScreen = lazyNamed(() => import("@/screens/TaxScreen/TaxFormAuditTrailScreen"), "TaxFormAuditTrailScreen");
const TaxFormCostBasisScreen = lazyNamed(() => import("@/screens/TaxScreen/TaxFormCostBasisScreen"), "TaxFormCostBasisScreen");
const TaxFormHoldingPeriodsScreen = lazyNamed(() => import("@/screens/TaxScreen/TaxFormHoldingPeriodsScreen"), "TaxFormHoldingPeriodsScreen");
const TaxFormLossManagementScreen = lazyNamed(() => import("@/screens/TaxScreen/TaxFormLossManagementScreen"), "TaxFormLossManagementScreen");

// Portfolio / Trades / Integrations
const PositionsScreen = lazyNamed(() => import("@/screens/PortfolioScreen/PositionsScreen"), "PositionsScreen");
const TradesScreen = lazyNamed(() => import("@/screens/TradesScreen/TradesScreen"), "TradesScreen");
const IntegrationsListScreen = lazyNamed(() => import("@/screens/IntegrationsListScreen/IntegrationsListScreen"), "IntegrationsListScreen");
const SyncStatusScreen = lazyNamed(() => import("@/screens/SyncStatusScreen/SyncStatusScreen"), "SyncStatusScreen");
const IntegrationDetail = lazyNamed(() => import("@/screens/IntegrationDetail"), "IntegrationDetail");
const RawTransactionsScreen = lazyNamed(() => import("@/screens/TransactionAuditScreen/RawTransactionsScreen"), "RawTransactionsScreen");
const BridgeMatchesScreen = React.lazy(() => import("@/screens/BridgeMatchesScreen/BridgeMatchesScreen"));

// Inbox
const InboxScreen = lazyNamed(() => import("@/screens/InboxScreen/InboxScreen"), "InboxScreen");
const AdminInboxScreen = lazyNamed(() => import("@/screens/AdminInboxScreen/AdminInboxScreen"), "AdminInboxScreen");

// Settings / Plans / Other
const SettingsScreen = lazyNamed(() => import("@/screens/SettingsScreen"), "SettingsScreen");
const Plans = lazyNamed(() => import("@/screens/Plans/Plans"), "Plans");
const TaxYearCheckoutScreen = lazyNamed(
  () => import("@/screens/TaxYearCheckout"),
  "TaxYearCheckoutScreen",
);
const TaxYearCheckoutSuccessScreen = lazyNamed(
  () => import("@/screens/TaxYearCheckout"),
  "TaxYearCheckoutSuccessScreen",
);
const TaxYearCheckoutCanceledScreen = lazyNamed(
  () => import("@/screens/TaxYearCheckout"),
  "TaxYearCheckoutCanceledScreen",
);
const AdjustmentsScreen = lazyNamed(() => import("@/screens/AdjustmentsScreen"), "AdjustmentsScreen");
const SupportScreen = lazyNamed(() => import("@/screens/SupportScreen"), "SupportScreen");
const FormReconciliationScreen = lazyNamed(() => import("@/screens/FormReconciliationScreen"), "FormReconciliationScreen");
const ReconciliationResultsScreen = lazyNamed(() => import("@/screens/FormReconciliationScreen"), "ReconciliationResultsScreen");
// Form1099DAReconciliation merged into FormReconciliationScreen
const QuizScreen = lazyNamed(() => import("@/screens/Quiz"), "QuizScreen");
const PromoScreen = lazyNamed(() => import("@/screens/Quiz"), "PromoScreen");
const TaxProfessionalsLanding = lazyNamed(() => import("@/screens/TaxProfessionals/TaxProfessionalsLanding"), "TaxProfessionalsLanding");
const AccountantClients = lazyNamed(() => import("@/screens/Accountant/AccountantClients"), "AccountantClients");
const AccountantOnboarding = lazyNamed(() => import("@/screens/Accountant/AccountantOnboarding"), "AccountantOnboarding");
/** Handles redirect param on root path - redirects to specified path if authenticated */
const RedirectHandler = () => {
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth0();
  const params = new URLSearchParams(location.search);
  const redirectParam = params.get('redirect');

  // If authenticated and has redirect param, redirect immediately
  if (redirectParam && isAuthenticated && !isLoading) {
    console.log("[routes] handling redirect param:", redirectParam);
    return <Redirect to={redirectParam} />;
  }

  // If still loading auth, show nothing (App.js will handle unauthenticated case)
  if (isLoading) {
    return null;
  }

  // If authenticated but no redirect, go to dashboard
  if (isAuthenticated) {
    return <Redirect to="/dashboard" />;
  }

  // If not authenticated, App.js should have handled this already
  return <Redirect to="/" />;
};

export const Routes = () => {
  // analytics
  const location = useLocation();
  const sessionStartRef = useRef<number | null>(null);
  const pageViewsRef = useRef(0);
  const eventsCountRef = useRef(0);

  const showIAP = useFeatureFlag(FEATURE_FLAG_KEYS.WEB_SHOW_IAP);
  const { isInForcedOnboarding } = useOnboardingGate();

  // Initialize attribution tracking on app load
  useEffect(() => {
    AttributionTracker.init();
  }, []);

  console.log("web showIAP", showIAP);
  const {
    user,
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
    loginWithRedirect,
  } = useAuth0();

  // Track session start and setup beforeunload listener
  useEffect(() => {
    if (isAuthenticated && !isLoading && !sessionStartRef.current) {
      sessionStartRef.current = Date.now();

      // Track session started
      trackEvent(SESSION_EVENTS.SESSION_STARTED, {
        user_agent: navigator.userAgent,
        source: "web",
      });

      // Signup attribution is persisted by useAttributionTracking in App, after
      // obtaining its bearer token. A second POST here raced that auth setup.

      // Setup beforeunload listener for session end
      const handleBeforeUnload = () => {
        const sessionDuration = sessionStartRef.current
          ? Date.now() - sessionStartRef.current
          : 0;

        trackEvent(SESSION_EVENTS.SESSION_ENDED, {
          session_duration_ms: sessionDuration,
          page_views: pageViewsRef.current,
          events_count: eventsCountRef.current,
          source: "web",
        });
      };

      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [isAuthenticated, isLoading]);

  useEffect(() => {
    // console.log("[analytics] route has been changed", location?.pathname);
    // segment analytics

    if (isAuthenticated && !isLoading) {
      analyticsPage("navigate", location?.pathname);

      // Increment page views counter
      pageViewsRef.current += 1;
      eventsCountRef.current += 1;

      // Track screen viewed for major navigation events
      trackEvent(NAVIGATION_EVENTS.SCREEN_VIEWED, {
        screen_name: location?.pathname,
        source: "web",
      });
    }
    // window.gtag("event", "page_view", {
    //   page_path: location.pathname + location.search,
    // });
  }, [isAuthenticated, isLoading, location?.pathname, location.search]);

  const fallback = (
    <div className="flex w-full justify-center items-center h-64">
      <Loading />
    </div>
  );

  return (
    <Suspense fallback={fallback}>
    <Switch>
      {/* Handle redirect param on root path */}
      <Route exact path="/">
        {location.search.includes('redirect=') ? <RedirectHandler /> : <Redirect to={{
          pathname: isInForcedOnboarding ? "/quiz" : "/dashboard",
          search: location.search
        }} />}
      </Route>

      {/* Attribution routes - must be before other routes */}
      <Route exact path="/tiktok" component={AttributionRedirect} />
      <Route exact path="/reddit" component={AttributionRedirect} />
      <Route exact path="/twitter" component={AttributionRedirect} />
      <Route exact path="/youtube" component={AttributionRedirect} />
      <Route exact path="/instagram" component={AttributionRedirect} />
      <Route exact path="/facebook" component={AttributionRedirect} />
      <Route exact path="/linkedin" component={AttributionRedirect} />
      <Route exact path="/discord" component={AttributionRedirect} />
      <Route exact path="/telegram" component={AttributionRedirect} />
      <Route exact path="/hackernews" component={AttributionRedirect} />
      <Route exact path="/producthunt" component={AttributionRedirect} />
      
      <ProtectedRoute exact component={QuizScreen} path="/quiz" />
      <ProtectedRoute exact component={PromoScreen} path="/promo" />
      <ProtectedRoute exact component={QuickStartScreen} path="/quickstart" />
      {/* Redirect to quiz if onboarding not complete */}
      {isInForcedOnboarding && <Redirect exact from="/dashboard" to="/quiz" />}
      <ProtectedRoute exact component={DashboardScreen} path="/dashboard" />

      {/* Transactions route */}
      <ProtectedRoute exact component={TradesScreen} path="/transactions" />

      {/* Integrations route - unified exchanges + wallets */}
      <ProtectedRoute exact component={IntegrationsListScreen} path="/integrations" />
      <ProtectedRoute exact component={SyncStatusScreen} path="/sync-status" />

      {/* root */}
      {/* don't have `exact` prop on settings bc it won't match and do to the sub-routes */}
      <ProtectedRoute path="/settings" component={SettingsScreen} />
      {/* <ProtectedRoute exact path="/watch" component={Watch} returnTo="/watch" /> */}
      <Route exact path="/login">
        <Login />
      </Route>
      <Route exact path="/support" component={SupportScreen} />
      <Route exact path="/tax-professionals" component={TaxProfessionalsLanding} />
      <ProtectedRoute exact path="/a/clients" component={AccountantClients} />
      <ProtectedRoute exact path="/a/onboarding" component={AccountantOnboarding} />

      {/* Inbox */}
      <ProtectedRoute exact path="/inbox" component={InboxScreen} />
      <ProtectedRoute exact path="/admin/inbox" component={AdminInboxScreen} />
      <Route exact path="/verify-email">
        <VerifyEmail />
      </Route>
      {/* You can add a new ProtectedRoute for /tax/pro before the existing ProtectedRoute for /tax. The order of routes is important in react-router-dom.  */}
      <PremiumTaxRoute path="/tax/pro" component={ProTaxScreen} trigger="tax_pro" />
      <PremiumTaxRoute path="/tax/safe-harbor" component={TaxSafeHarborScreen} trigger="tax_safe_harbor" />
      <PremiumTaxRoute
        path="/tax/loss-harvesting"
        component={TaxLossHarvesting}
        trigger="tax_loss_harvesting"
      />
      <ProtectedRoute exact path="/tax/excluded" component={ExcludedTransactionsView} />
      <PremiumTaxRoute exact path="/tax/audit" component={TaxAuditScreen} trigger="tax_audit" />
      <PremiumTaxRoute exact path="/tax/adjustments" component={AdjustmentsScreen} trigger="tax_adjustments" />
      <PremiumTaxRoute exact path="/tax/events" component={TaxEventsPage} trigger="tax_events" />
      <PremiumTaxRoute exact path="/tax/income" component={IncomeEventsPage} trigger="tax_income" />
      <PremiumTaxRoute exact path="/tax/asset/:symbol" component={TaxAssetBreakdownScreen} trigger="tax_asset_breakdown" />
      <PremiumTaxRoute
        exact
        path="/tax/:taxYear/report"
        component={TaxReportHtmlRedirect}
        trigger="tax_report_html"
      />
      <PremiumTaxRoute
        exact
        path="/tax/:taxYear/reports"
        component={TaxReportsOverviewScreen}
        trigger="tax_reports"
      />
      <PremiumTaxRoute
        exact
        path="/tax/:taxYear/form-reconciliation"
        component={FormReconciliationScreen}
        trigger="tax_form_reconciliation"
      />
      <PremiumTaxRoute
        exact
        path="/tax/:taxYear/form-reconciliation/:batchId"
        component={ReconciliationResultsScreen}
        trigger="tax_form_reconciliation"
      />
      <Redirect exact from="/tax/1099-da" to={`/tax/${CURRENT_TAX_YEAR}/form-reconciliation`} />
      <Redirect exact from="/tax/reports" to={`/tax/${CURRENT_TAX_YEAR}/reports`} />
      <PremiumTaxRoute
        exact
        path="/tax/reports/:taxYear/html"
        component={TaxReportHtmlScreen}
        trigger="tax_report_html"
      />
      <PremiumTaxRoute
        exact
        path="/tax/reports/:taxYear/forms/us/8949"
        component={TaxForm8949Screen}
        trigger="tax_form_8949"
      />
      <PremiumTaxRoute
        exact
        path="/tax/reports/:taxYear/forms/us/8949-detail"
        component={TaxForm8949ReplicaScreen}
        trigger="tax_form_8949"
      />
      <PremiumTaxRoute
        exact
        path="/tax/reports/:taxYear/forms/us/schedule-d"
        component={TaxFormScheduleDScreen}
        trigger="tax_form_schedule_d"
      />
      <PremiumTaxRoute
        exact
        path="/tax/reports/:taxYear/forms/us/1040"
        component={TaxForm1040Screen}
        trigger="tax_form_1040"
      />
      <PremiumTaxRoute
        exact
        path="/tax/reports/:taxYear/forms/us/income-statement"
        component={TaxFormUsIncomeStatementScreen}
        trigger="tax_form_income_statement"
      />
      <PremiumTaxRoute
        exact
        path="/tax/reports/:taxYear/forms/uk"
        component={TaxFormUKScreen}
        trigger="tax_form_uk"
      />
      <PremiumTaxRoute
        exact
        path="/tax/reports/:taxYear/forms/uk/sa100"
        component={TaxFormSA100Screen}
        trigger="tax_form_sa100"
      />
      <PremiumTaxRoute
        exact
        path="/tax/reports/:taxYear/forms/uk/income-statement"
        component={TaxFormIncomeStatementScreen}
        trigger="tax_form_income_statement"
      />
      <PremiumTaxRoute
        exact
        path="/tax/reports/:taxYear/forms/uk/sa108"
        component={TaxFormSA108Screen}
        trigger="tax_form_sa108"
      />
      <PremiumTaxRoute
        exact
        path="/tax/reports/:taxYear/forms/uk/computation"
        component={TaxComputationReportScreen}
        trigger="tax_form_computation"
      />
      <PremiumTaxRoute
        exact
        path="/tax/reports/:taxYear/forms/ca/schedule-3"
        component={TaxFormSchedule3Screen}
        trigger="tax_form_schedule_3"
      />
      <PremiumTaxRoute
        exact
        path="/tax/reports/:taxYear/forms/uk/loss-writeoffs"
        component={TaxFormLossWriteoffsScreen}
        trigger="tax_form_loss_writeoffs"
      />
      <PremiumTaxRoute
        exact
        path="/tax/reports/:taxYear/forms/uk/audit-trail"
        component={TaxFormAuditTrailScreen}
        trigger="tax_form_audit_trail"
      />
      <PremiumTaxRoute
        exact
        path="/tax/reports/:taxYear/forms/uk/cost-basis"
        component={TaxFormCostBasisScreen}
        trigger="tax_form_cost_basis"
      />
      <PremiumTaxRoute
        exact
        path="/tax/reports/:taxYear/forms/uk/holding-periods"
        component={TaxFormHoldingPeriodsScreen}
        trigger="tax_form_holding_periods"
      />
      <PremiumTaxRoute
        exact
        path="/tax/reports/:taxYear/forms/uk/loss-management"
        component={TaxFormLossManagementScreen}
        trigger="tax_form_loss_management"
      />
      <ProtectedRoute
        exact
        component={TaxYearCheckoutScreen}
        path="/tax/checkout"
      />
      <ProtectedRoute
        exact
        component={TaxYearCheckoutSuccessScreen}
        path="/tax/checkout/success"
      />
      <ProtectedRoute
        exact
        component={TaxYearCheckoutCanceledScreen}
        path="/tax/checkout/canceled"
      />
      <Redirect exact from="/tax/cgt" to="/tax" />
      <ProtectedRoute exact path="/tax" component={TaxScreen} />
      {/* go to login with /:plan params in the url  */}
      <Route path="/signup/:plan" component={Login} />
      {/* optional parameters to filter down on exchange/coin */}
      {/* <ProtectedRoute
        exact
        path="/trades"
        // path="/trades/:?exchange/:asset?"
        component={TradesScreen}
      /> */}
      <ProtectedRoute
        path="/portfolio/:exchange?/:asset?"
        component={PositionsScreen}
      />
      {/*
      /portfolio/:exchange/:coin
      e.g.
      /portfolio/all/BTC
      /portfolio/binance/DOGE
      /portfolio/coinbase/

      */}
      {/* <ProtectedRoute exact path="/portfolio" component={PositionsScreen} /> */}
      <Route exact path="/logout">
        <Logout />
      </Route>
      {(showIAP || true) && (
        <Route exact path="/plans">
            <Plans />
          {/* <UpgradeScreen /> */}
          {/* <StripeCheckout /> */}
        </Route>
      )}
      <ProtectedRoute
        path="/integration/:exchangeId"
        component={IntegrationDetail}
      />

      <ProtectedRoute
        path="/transactions/:exchange"
        component={RawTransactionsScreen}
      />
      {/* <ProtectedRoute
        path="/audit/transaction/:extTxnId"
        component={TransactionAuditScreen}
      /> */}
      <Redirect exact from="/audit" to="/tax/audit" />

      {/* Bridge detection routes */}
      <ProtectedRoute exact path="/bridge-matches" component={BridgeMatchesScreen} />

      {/* Wallets route redirects to integrations with blockchain filter */}
      <ProtectedRoute exact path="/wallets" render={() => <Redirect to="/integrations?type=blockchain" />} />

      {/* Stripe checkout callbacks (success/cancel pages) */}
      <ProtectedRoute
        exact
        path="/checkout/success"
        component={StripeSuccess}
      />
      <ProtectedRoute exact path="/checkout/cancel" component={StripeCancel} />
      <ProtectedRoute exact path="/feedback" component={FeedbackScreen} />
      {/* <Route component={TradesScreen} /> */}
      {/* default catch-all react-router */}
      {/* catch-all for unmatched requests */}
      <Route exact path="/coinbase-auth" component={CoinbaseAuth} />

      {/* onboarding */}

      <Redirect
        to={{
          pathname: "/",
          // state: { referrer: location },
        }}
      />
      {/* <Route path="/about">
            <About />
          </Route>
          <Route path="/dashboard">
            <Dashboard />
          </Route> */}
    </Switch>
    </Suspense>
  );
};
